import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, Loader2 } from "lucide-react";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Avatar from "./Avatar";

const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const MAX_SIZE_MB = 5;

export default function ProfilePhotoUploader() {
  const { user, setUser } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const onPick = () => ref.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error("Formats acceptés : JPG, PNG, WebP");
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Image trop lourde (max ${MAX_SIZE_MB} Mo)`);
      return;
    }
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(data.user);
      toast.success("Photo de profil mise à jour");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      setPreview(null);
      URL.revokeObjectURL(localUrl);
    }
  };

  const onRemove = async () => {
    if (!user?.picture) return;
    if (!window.confirm("Supprimer votre photo de profil ?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/me/avatar");
      setUser(data);
      toast.success("Photo supprimée");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="profile-photo-uploader">
      <div className="relative">
        {preview ? (
          <img src={preview} alt="Aperçu" className="w-20 h-20 rounded-full object-cover ring-2 ring-blanc/40" />
        ) : (
          <Avatar name={user?.name} picture={user?.picture} size={80} ring />
        )}
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-ocean/40 rounded-full">
            <Loader2 className="w-5 h-5 text-blanc animate-spin" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <button
          onClick={onPick}
          disabled={busy}
          data-testid="avatar-upload-btn"
          className="inline-flex items-center gap-1.5 text-xs font-bold bg-blanc/15 hover:bg-blanc/25 text-blanc px-3 py-1.5 rounded-full tactile disabled:opacity-50"
        >
          <Camera className="w-3.5 h-3.5" /> {user?.picture ? "Changer" : "Ajouter une photo"}
        </button>
        {user?.picture && (
          <button
            onClick={onRemove}
            disabled={busy}
            data-testid="avatar-remove-btn"
            className="inline-flex items-center gap-1.5 text-xs text-blanc/70 hover:text-blanc disabled:opacity-50"
          >
            <Trash2 className="w-3 h-3" /> Supprimer
          </button>
        )}
        <input
          ref={ref}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onChange}
          className="hidden"
          data-testid="avatar-file-input"
        />
      </div>
    </div>
  );
}
